import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiCheckCircle,
  FiX,
  FiCheck,
  FiShare2,
  FiRefreshCw,
  FiBookOpen,
  FiHeadphones,
  FiEdit2,
  FiMic,
  FiAlertCircle,
  FiTarget,
  FiVolume2,
} from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import Sidebar from '../../Sidebar';
import WritingResults from './WritingResults';
import SpeakingResults from './SpeakingResults';
import '../English.css';

// Import test data
import listeningData from '../../../assets/mock-data/listening.json';
import readingData from '../../../assets/mock-data/reading.json';
import writingData from '../../../assets/mock-data/writing.json';
import speakingData from '../../../assets/mock-data/speaking.json';

import {
  API_BASE_URL,
  getAccountScopedLocalStorageKey,
  pronounceWord,
  getAuthHeaders,
} from '../../../utils/utils';
import { getStoredUser } from '../../../utils/authSession';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import ToeflNextStepCard from '../toefl/ToeflNextStepCard';

const getScopedLocalStorageItem = (key) => {
  const scopedKey = getAccountScopedLocalStorageKey(key);
  return scopedKey ? localStorage.getItem(scopedKey) : null;
};

const setScopedLocalStorageItem = (key, value) => {
  const scopedKey = getAccountScopedLocalStorageKey(key);
  if (!scopedKey) return false;
  localStorage.setItem(scopedKey, value);
  return true;
};

// Get test data by type and ID
const getTestData = (type, testId) => {
  const data = {
    listening: listeningData,
    reading: readingData,
    writing: writingData,
    speaking: speakingData,
  };

  const tests = data[type] || [];
  return tests.find((test) => test.id === parseInt(testId));
};

// Band score calculation functions for different test types
const calculateBandScore = (rawScore, testType, totalQuestions = 40) => {
  if (testType === 'listening' || testType === 'reading') {
    // IELTS Listening/Reading band score conversion
    if (rawScore >= 39) return 9.0;
    if (rawScore >= 37) return 8.5;
    if (rawScore >= 35) return 8.0;
    if (rawScore >= 32) return 7.5;
    if (rawScore >= 30) return 7.0;
    if (rawScore >= 26) return 6.5;
    if (rawScore >= 23) return 6.0;
    if (rawScore >= 18) return 5.5;
    if (rawScore >= 16) return 5.0;
    if (rawScore >= 13) return 4.5;
    if (rawScore >= 11) return 4.0;
    if (rawScore >= 9) return 3.5;
    if (rawScore >= 7) return 3.0;
    if (rawScore >= 5) return 2.5;
    if (rawScore >= 4) return 2.0;
    return 1.0;
  }

  // For writing and speaking, band scores are typically given directly
  // This is a simplified calculation
  const percentage = (rawScore / totalQuestions) * 100;
  if (percentage >= 90) return 9.0;
  if (percentage >= 85) return 8.5;
  if (percentage >= 80) return 8.0;
  if (percentage >= 75) return 7.5;
  if (percentage >= 70) return 7.0;
  if (percentage >= 65) return 6.5;
  if (percentage >= 60) return 6.0;
  if (percentage >= 55) return 5.5;
  if (percentage >= 50) return 5.0;
  if (percentage >= 45) return 4.5;
  if (percentage >= 40) return 4.0;
  if (percentage >= 35) return 3.5;
  if (percentage >= 30) return 3.0;
  if (percentage >= 25) return 2.5;
  if (percentage >= 20) return 2.0;
  return 1.0;
};

// Get test icon by type
const getTestIcon = (testType) => {
  switch (testType) {
    case 'listening':
      return <FiHeadphones size={24} />;
    case 'reading':
      return <FiBookOpen size={24} />;
    case 'writing':
      return <FiEdit2 size={24} />;
    case 'speaking':
      return <FiMic size={24} />;
    default:
      return <FiCheckCircle size={24} />;
  }
};

export default function Results() {
  const { testType, testId } = useParams();
  const navigate = useNavigate();
  const isSpeakingRoute = testType === 'speaking';
  const [testResults, setTestResults] = useState(null);
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allAttempts, setAllAttempts] = useState([]);
  const [selectedAttemptIndex, setSelectedAttemptIndex] = useState(0);

  // Helper function to map backend result to frontend structure
  const mapBackendResult = (backendResult) => ({
    testId: backendResult.test_id,
    testType: backendResult.test_type,
    rawScore: backendResult.raw_score,
    bandScore: backendResult.band_score,
    totalQuestions: backendResult.total_questions,
    completedAt: backendResult.created_at,
    startTime: backendResult.start_time,
    endTime: backendResult.end_time,
    timeTakenSeconds: backendResult.time_taken_seconds,
    answers: backendResult.user_answers || {},
    checkboxAnswers: backendResult.checkbox_answers || {},
    results: backendResult.detailed_results || [],
    essays: backendResult.essays || {},
    assessmentData: backendResult.assessment_data || null, // For writing tests
    attemptId: backendResult.id, // Store the backend ID for reference
    // New fields for listening feedback
    missedWordsFeedback: backendResult.missed_words_feedback || null,
    missedWordsActions: backendResult.missed_words_actions || null,
  });

  useEffect(() => {
    const fetchTestResults = async () => {
      try {
        setLoading(true);

        // Speaking results have a dedicated loader that understands backend attempt IDs.
        // Do not try to resolve them against the static speaking mock JSON here.
        if (isSpeakingRoute) {
          return;
        }

        // Get test data from JSON files
        const test = getTestData(testType, testId);
        console.log(testType, testId, test);

        if (testType === 'writing') {
          // For writing tests, test template is optional -- results can load without it
          if (test) setTestData(test);

          // Load results from localStorage
          const completedTests = getScopedLocalStorageItem('completedTests');
          if (completedTests) {
            const parsed = JSON.parse(completedTests);
            const typeTests = parsed[testType] || [];
            const completedTest = typeTests.find(
              (t) => String(t.testId) === String(testId)
            );

            if (completedTest) {
              setTestData((prev) => prev || {
                id: parseInt(testId),
                title: `Writing Test ${testId}`,
                sections: [],
              });
              setTestResults({
                testId: parseInt(testId),
                testType: testType,
                rawScore: completedTest.score || 0,
                bandScore:
                  completedTest.bandScore ||
                  calculateBandScore(completedTest.score || 0, testType),
                totalQuestions: test?.totalQuestions || 40,
                completedAt: completedTest.completedAt,
                answers: {},
                results: [],
                essays: completedTest.essays || {},
                assessmentData: completedTest.assessmentData || null,
                taskData: completedTest.taskData || null,
                userEmail: completedTest.userEmail || '',
              });
            } else {
              setError(
                'No results found for this test. Please complete the test first.'
              );
            }
          } else {
            setError(
              'No results found for this test. Please complete the test first.'
            );
          }
          setLoading(false);
          return;
        }

        if (!test) {
          setError(`Test with ID ${testId} not found for ${testType}`);
          setLoading(false);
          return;
        }
        setTestData(test);

        if (testType === 'speaking') {
          const testresults = JSON.parse(
            getScopedLocalStorageItem('userProgress') || '[]'
          );
          console.log('testresults', testresults);
          if (testresults.length > 0) {
            setTestResults(testresults[0]);
          } else {
            setError(
              'No results found for this test. Please complete the test first.'
            );
          }
          setLoading(false);
          return;
        }

        // Try to get results from backend first
        try {
          const user = getStoredUser();
          const response = await fetch(
            `${API_BASE_URL}/api/get-test-results?testType=${testType}&testId=${testId}`,
            { headers: getAuthHeaders() }
          );

          if (response.ok) {
            const backendData = await response.json();
            console.log('Backend response:', backendData);

            // Check if we have results
            if (
              backendData.success &&
              backendData.results &&
              backendData.results.length > 0
            ) {
              // Map all results to our expected structure
              const mappedAttempts = backendData.results.map((result) =>
                mapBackendResult(result)
              );
              setAllAttempts(mappedAttempts);

              // Set the latest attempt (first in array) as default
              setSelectedAttemptIndex(0);
              setTestResults(mappedAttempts[0]);
            } else {
              setError(
                `No results found for this test. Please complete the test first.`
              );
              throw new Error('No results found in backend response');
            }
          } else {
            throw new Error('Results not found in backend');
          }
        } catch (backendError) {
          console.log(
            'Backend results not found, checking localStorage...',
            backendError
          );

          // Fallback to localStorage for recently completed tests
          const completedTests = getScopedLocalStorageItem('completedTests');
          if (completedTests) {
            const parsed = JSON.parse(completedTests);
            const typeTests = parsed[testType] || [];
            const completedTest = typeTests.find(
              (t) => t.testId === parseInt(testId)
            );

            if (completedTest) {
              // Create a mock result structure for display
              setTestResults({
                testId: parseInt(testId),
                testType: testType,
                rawScore: completedTest.score || 0,
                bandScore:
                  completedTest.bandScore ||
                  calculateBandScore(completedTest.score || 0, testType),
                totalQuestions: test.totalQuestions || 40,
                completedAt: completedTest.completedAt,
                answers: {}, // Would need to be stored separately
                results: [], // Would need detailed results
                essays: completedTest.essays || {},
                assessmentData: completedTest.assessmentData || null,
                taskData: completedTest.taskData || null,
              });
            } else {
              setError(
                'No results found for this test. Please complete the test first.'
              );
            }
          } else {
            setError(
              'No results found for this test. Please complete the test first.'
            );
          }
        }
      } catch (err) {
        console.error('Error fetching test results:', err);
        setError('Error loading test results. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (testType && testId) {
      fetchTestResults();
    }
  }, [isSpeakingRoute, testType, testId]);

  if (isSpeakingRoute) {
    return <SpeakingResults />;
  }

  const handleRetake = () => {
    // Save exam info and navigate to test
    const examInfo = {
      id: parseInt(testId),
      title: testData?.title || `${testType} Test ${testId}`,
      type: testType,
      startTime: new Date().toISOString(),
    };
    setScopedLocalStorageItem('englishExam', JSON.stringify(examInfo));
    navigate(`/exams/english/${testType}`);
  };

  const handleShare = () => {
    const shareText = `Check out my ${testType} test results: Band Score ${testResults?.bandScore || 'N/A'}`;
    const shareUrl = window.location.href;

    if (navigator.share) {
      navigator
        .share({
          title: `${testType} Test Results`,
          text: shareText,
          url: shareUrl,
        })
        .catch(console.error);
    } else {
      const shareContent = `${shareText}\n${shareUrl}`;
      navigator.clipboard
        .writeText(shareContent)
        .then(() => {
          alert('Results link copied to clipboard!');
        })
        .catch(() => {
          prompt('Copy this link to share:', shareContent);
        });
    }
  };

  const handleAttemptChange = (index) => {
    setSelectedAttemptIndex(index);
    setTestResults(allAttempts[index]);
  };

  if (loading) {
    return (
      <div className="english-layout">
        <Sidebar />
        <main className="english-main">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '60vh',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <div
              className="loading-spinner"
              style={{ width: 48, height: 48 }}
            ></div>
            <p style={{ color: '#666', fontSize: 18 }}>
              Loading test results...
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="english-layout">
        <Sidebar />
        <main className="english-main">
          <div
            style={{
              textAlign: 'center',
              padding: '60px 32px',
              color: '#c62828',
              background: '#ffebee',
              borderRadius: 12,
              border: '1px solid #ffcdd2',
              margin: '2rem auto',
              maxWidth: 600,
            }}
          >
            <FiX size={48} style={{ marginBottom: 16 }} />
            <h2 style={{ marginBottom: 16 }}>Results Not Found</h2>
            <p style={{ marginBottom: 24, fontSize: 16 }}>
              {getErrorMessage(error, 'Unable to load results.')}
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
              <button
                className="btn-lwz-primary"
                onClick={() =>
                  navigate('/exams/english/progression', {
                    state: { activeCategory: 'progression' },
                  })
                }
              >
                <FiArrowLeft /> Back to Tests
              </button>
              <button className="btn-lwz-outline" onClick={handleRetake}>
                <FiRefreshCw /> Take Test
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (testType === 'writing') {
    return <WritingResults testResults={testResults} testData={testData} />;
  }

  return (
    <div className="english-layout">
      <Sidebar />
      <main className="english-main">
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 32,
            padding: '24px 32px',
            background: 'var(--liwaza-surface-container)',
            borderRadius: 16,
            border: '1px solid var(--liwaza-outline-variant)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              onClick={() =>
                navigate('/exams/english/progression', {
                  state: { activeCategory: 'progression' },
                })
              }
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--liwaza-primary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                fontSize: 16,
                padding: 8,
              }}
            >
              <FiArrowLeft size={20} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ color: 'var(--liwaza-primary)' }}>
                {getTestIcon(testType)}
              </span>
              <div>
                <h1
                  style={{
                    margin: 0,
                    fontSize: 24,
                    fontWeight: 700,
                    color: 'var(--liwaza-primary)',
                    textTransform: 'capitalize',
                  }}
                >
                  {testType} Test Results
                </h1>
                <p
                  style={{
                    margin: 0,
                    color: 'var(--liwaza-secondary)',
                    fontSize: 14,
                  }}
                >
                  {testData?.title || `Test ID: ${testId}`}
                </p>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn-lwz-outline" onClick={handleShare}>
              <FiShare2 /> Share Results
            </button>
            <button className="btn-lwz-primary" onClick={handleRetake}>
              <FiRefreshCw /> Retake Test
            </button>
          </div>
        </div>

        {/* Attempt Selector */}
        {allAttempts.length > 1 && (
          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              borderRadius: 12,
              border: '1px solid var(--liwaza-outline-variant)',
              padding: '16px 24px',
              marginBottom: 32,
              display: 'flex',
              alignItems: 'center',
              gap: 16,
            }}
          >
            <span
              style={{
                fontWeight: 600,
                color: 'var(--liwaza-primary)',
                fontSize: 16,
              }}
            >
              Test Attempt:
            </span>
            <select
              value={selectedAttemptIndex}
              onChange={(e) => handleAttemptChange(parseInt(e.target.value))}
              style={{
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid var(--liwaza-outline-variant)',
                background: 'var(--liwaza-surface-container-highest)',
                color: 'var(--liwaza-on-surface)',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                minWidth: 200,
              }}
            >
              {allAttempts.map((attempt, index) => (
                <option key={index} value={index}>
                  Attempt {allAttempts.length - index} -{' '}
                  {new Date(attempt.completedAt).toLocaleDateString()} at{' '}
                  {new Date(attempt.completedAt).toLocaleTimeString()}
                  (Band Score: {attempt.bandScore})
                </option>
              ))}
            </select>
            <div
              style={{
                background:
                  selectedAttemptIndex === 0
                    ? 'var(--liwaza-primary)'
                    : 'var(--liwaza-surface-container-high)',
                color:
                  selectedAttemptIndex === 0
                    ? 'white'
                    : 'var(--liwaza-secondary)',
                padding: '4px 12px',
                borderRadius: 16,
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              {selectedAttemptIndex === 0 ? 'Latest' : 'Previous'}
            </div>
          </div>
        )}

        {/* Score Overview */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 24,
            marginBottom: 32,
          }}
        >
          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              padding: 24,
              borderRadius: 16,
              border: '2px solid var(--liwaza-primary)',
              textAlign: 'center',
            }}
          >
            <h3
              style={{
                margin: '0 0 8px 0',
                color: 'var(--liwaza-secondary)',
                fontSize: 14,
                fontWeight: 500,
                textTransform: 'uppercase',
              }}
            >
              Band Score
            </h3>
            <div
              style={{
                fontSize: 48,
                fontWeight: 700,
                color: 'var(--liwaza-primary)',
                lineHeight: 1,
              }}
            >
              {testResults?.bandScore || 'N/A'}
            </div>
          </div>

          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              padding: 24,
              borderRadius: 16,
              border: '1px solid var(--liwaza-outline-variant)',
              textAlign: 'center',
            }}
          >
            <h3
              style={{
                margin: '0 0 8px 0',
                color: 'var(--liwaza-secondary)',
                fontSize: 14,
                fontWeight: 500,
                textTransform: 'uppercase',
              }}
            >
              Raw Score
            </h3>
            <div
              style={{
                fontSize: 32,
                fontWeight: 600,
                color: 'var(--liwaza-on-surface)',
              }}
            >
              {testResults?.rawScore || 0} / {testResults?.totalQuestions || 40}
            </div>
          </div>

          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              padding: 24,
              borderRadius: 16,
              border: '1px solid var(--liwaza-outline-variant)',
              textAlign: 'center',
            }}
          >
            <h3
              style={{
                margin: '0 0 8px 0',
                color: 'var(--liwaza-secondary)',
                fontSize: 14,
                fontWeight: 500,
                textTransform: 'uppercase',
              }}
            >
              Accuracy
            </h3>
            <div
              style={{
                fontSize: 32,
                fontWeight: 600,
                color: 'var(--liwaza-on-surface)',
              }}
            >
              {testResults
                ? Math.round(
                    (testResults.rawScore / testResults.totalQuestions) * 100
                  )
                : 0}
              %
            </div>
          </div>

          {testResults?.timeTakenSeconds && (
            <div
              style={{
                background: 'var(--liwaza-surface-container)',
                padding: 24,
                borderRadius: 16,
                border: '1px solid var(--liwaza-outline-variant)',
                textAlign: 'center',
              }}
            >
              <h3
                style={{
                  margin: '0 0 8px 0',
                  color: 'var(--liwaza-secondary)',
                  fontSize: 14,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                }}
              >
                Time Taken
              </h3>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 600,
                  color: 'var(--liwaza-on-surface)',
                }}
              >
                {Math.floor(testResults.timeTakenSeconds / 60)}m{' '}
                {testResults.timeTakenSeconds % 60}s
              </div>
            </div>
          )}

          {testResults?.completedAt && (
            <div
              style={{
                background: 'var(--liwaza-surface-container)',
                padding: 24,
                borderRadius: 16,
                border: '1px solid var(--liwaza-outline-variant)',
                textAlign: 'center',
              }}
            >
              <h3
                style={{
                  margin: '0 0 8px 0',
                  color: 'var(--liwaza-secondary)',
                  fontSize: 14,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                }}
              >
                Completed
              </h3>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: 'var(--liwaza-on-surface)',
                }}
              >
                {new Date(testResults.completedAt).toLocaleDateString()}
                <br />
                <small style={{ color: 'var(--liwaza-secondary)' }}>
                  {new Date(testResults.completedAt).toLocaleTimeString()}
                </small>
              </div>
            </div>
          )}
        </div>

        <ToeflNextStepCard bandScore={testResults?.bandScore} />

        {/* Attempt Comparison (only show for previous attempts) */}
        {allAttempts.length > 1 && selectedAttemptIndex > 0 && (
          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              borderRadius: 12,
              border: '1px solid var(--liwaza-outline-variant)',
              padding: 24,
              marginBottom: 32,
            }}
          >
            <h3
              style={{
                margin: '0 0 16px 0',
                fontSize: 18,
                fontWeight: 600,
                color: 'var(--liwaza-primary)',
              }}
            >
              Progress Comparison
            </h3>
            {(() => {
              const currentAttempt = allAttempts[selectedAttemptIndex];
              const latestAttempt = allAttempts[0];
              const scoreDiff =
                latestAttempt.rawScore - currentAttempt.rawScore;
              const bandDiff =
                latestAttempt.bandScore - currentAttempt.bandScore;

              return (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 16,
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: 14,
                        color: 'var(--liwaza-secondary)',
                        marginBottom: 4,
                      }}
                    >
                      Raw Score Change
                    </div>
                    <div
                      style={{
                        fontSize: 24,
                        fontWeight: 600,
                        color:
                          scoreDiff > 0
                            ? '#1b8a3b'
                            : scoreDiff < 0
                              ? '#c62828'
                              : 'var(--liwaza-on-surface)',
                      }}
                    >
                      {scoreDiff > 0 ? '+' : ''}
                      {scoreDiff}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: 14,
                        color: 'var(--liwaza-secondary)',
                        marginBottom: 4,
                      }}
                    >
                      Band Score Change
                    </div>
                    <div
                      style={{
                        fontSize: 24,
                        fontWeight: 600,
                        color:
                          bandDiff > 0
                            ? '#1b8a3b'
                            : bandDiff < 0
                              ? '#c62828'
                              : 'var(--liwaza-on-surface)',
                      }}
                    >
                      {bandDiff > 0 ? '+' : ''}
                      {bandDiff.toFixed(1)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: 14,
                        color: 'var(--liwaza-secondary)',
                        marginBottom: 4,
                      }}
                    >
                      Latest Attempt
                    </div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 500,
                        color: 'var(--liwaza-on-surface)',
                      }}
                    >
                      {new Date(latestAttempt.completedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Detailed Results */}
        {testResults?.results && testResults.results.length > 0 && (
          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              borderRadius: 16,
              border: '1px solid var(--liwaza-outline-variant)',
              overflow: 'hidden',
              marginBottom: 32,
            }}
          >
            <div
              style={{
                padding: 24,
                borderBottom: '1px solid var(--liwaza-outline-variant)',
                background: 'var(--liwaza-surface-container-high)',
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 600,
                  color: 'var(--liwaza-primary)',
                }}
              >
                Detailed Answer Review
              </h2>
            </div>

            <div style={{ padding: 24 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr
                    style={{
                      background: 'var(--liwaza-surface-container-highest)',
                    }}
                  >
                    <th
                      style={{
                        padding: 12,
                        textAlign: 'left',
                        fontWeight: 600,
                        color: 'var(--liwaza-primary)',
                        border: '1px solid var(--liwaza-outline-variant)',
                      }}
                    >
                      Question #
                    </th>
                    <th
                      style={{
                        padding: 12,
                        textAlign: 'left',
                        fontWeight: 600,
                        color: 'var(--liwaza-primary)',
                        border: '1px solid var(--liwaza-outline-variant)',
                      }}
                    >
                      Your Answer
                    </th>
                    <th
                      style={{
                        padding: 12,
                        textAlign: 'left',
                        fontWeight: 600,
                        color: 'var(--liwaza-primary)',
                        border: '1px solid var(--liwaza-outline-variant)',
                      }}
                    >
                      Correct Answer
                    </th>
                    <th
                      style={{
                        padding: 12,
                        textAlign: 'center',
                        fontWeight: 600,
                        color: 'var(--liwaza-primary)',
                        border: '1px solid var(--liwaza-outline-variant)',
                      }}
                    >
                      Result
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {testResults.results.map((result, index) => (
                    <tr
                      key={index}
                      style={{
                        background: result.isCorrect ? '#e8f5e8' : '#ffeaea',
                      }}
                    >
                      <td
                        style={{
                          padding: 12,
                          fontWeight: 600,
                          border: '1px solid var(--liwaza-outline-variant)',
                        }}
                      >
                        {result.number}
                      </td>
                      <td
                        style={{
                          padding: 12,
                          border: '1px solid var(--liwaza-outline-variant)',
                        }}
                      >
                        {result.user || '(No answer)'}
                      </td>
                      <td
                        style={{
                          padding: 12,
                          border: '1px solid var(--liwaza-outline-variant)',
                        }}
                      >
                        {result.correct}
                      </td>
                      <td
                        style={{
                          padding: 12,
                          textAlign: 'center',
                          border: '1px solid var(--liwaza-outline-variant)',
                        }}
                      >
                        {result.isCorrect ? (
                          <FiCheck style={{ color: '#1b8a3b' }} size={20} />
                        ) : (
                          <FiX style={{ color: '#c62828' }} size={20} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Missed Words Feedback (Listening tests only) */}
        {testType === 'listening' &&
          testResults?.missedWordsFeedback &&
          testResults.missedWordsFeedback.length > 0 && (
            <div
              style={{
                background: 'var(--liwaza-surface-container)',
                borderRadius: 16,
                border: '1px solid rgba(239, 68, 68, 0.2)',
                overflow: 'hidden',
                marginBottom: 32,
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: 'linear-gradient(90deg, #ef4444, #f97316)',
                }}
              />
              <div
                style={{
                  padding: 24,
                  borderBottom: '1px solid var(--liwaza-outline-variant)',
                  background: 'var(--liwaza-surface-container-high)',
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: 20,
                    fontWeight: 600,
                    color: '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <FiBookOpen /> Words You Missed
                  <span
                    style={{
                      fontSize: 12,
                      background: 'rgba(239,68,68,0.1)',
                      color: '#dc2626',
                      padding: '2px 8px',
                      borderRadius: 6,
                      fontWeight: 600,
                    }}
                  >
                    {testResults.missedWordsFeedback.length} word
                    {testResults.missedWordsFeedback.length !== 1 ? 's' : ''}
                  </span>
                </h2>
                <p
                  style={{
                    margin: '8px 0 0',
                    color: 'var(--liwaza-secondary)',
                    fontSize: 14,
                  }}
                >
                  Here are the words you got wrong, with pronunciation guides
                  and tips to recognize them.
                </p>
              </div>
              <div
                style={{
                  padding: 24,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                }}
              >
                {testResults.missedWordsFeedback.map((wordItem, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: '#fafafa',
                      borderRadius: 12,
                      padding: '16px 20px',
                      border: '1px solid rgba(0,0,0,0.06)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        marginBottom: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{ fontSize: '1.05rem', fontWeight: 700 }}>
                        {wordItem.word}
                      </span>
                      <button
                        onClick={() => pronounceWord(wordItem.word)}
                        title={`Hear pronunciation of "${wordItem.word}"`}
                        aria-label={`Pronounce ${wordItem.word}`}
                        style={{
                          background: 'rgba(103,80,164,0.1)',
                          border: 'none',
                          borderRadius: '50%',
                          width: 30,
                          height: 30,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: 'var(--liwaza-primary, #6750a4)',
                          transition: 'all 0.2s ease',
                          flexShrink: 0,
                        }}
                      >
                        <FiVolume2 size={14} />
                      </button>
                      {wordItem.phonetic && (
                        <span
                          style={{
                            fontSize: '0.85rem',
                            color: 'var(--liwaza-primary)',
                            fontStyle: 'italic',
                          }}
                        >
                          {wordItem.phonetic}
                        </span>
                      )}
                      {wordItem.part_of_speech && (
                        <span
                          style={{
                            fontSize: '0.72rem',
                            background: 'rgba(103,80,164,0.1)',
                            color: 'var(--liwaza-primary)',
                            padding: '2px 8px',
                            borderRadius: 6,
                            fontWeight: 500,
                          }}
                        >
                          {wordItem.part_of_speech}
                        </span>
                      )}
                    </div>
                    {wordItem.definition && (
                      <p
                        style={{
                          margin: '0 0 8px',
                          color: '#666',
                          fontSize: '0.85rem',
                          lineHeight: 1.5,
                        }}
                      >
                        {wordItem.definition}
                      </p>
                    )}
                    {wordItem.why_difficult && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 8,
                          padding: '8px 12px',
                          background: 'rgba(239,68,68,0.06)',
                          borderRadius: 8,
                          marginBottom: 8,
                          fontSize: '0.83rem',
                          color: '#dc2626',
                          lineHeight: 1.5,
                        }}
                      >
                        <FiAlertCircle
                          style={{ flexShrink: 0, marginTop: 2 }}
                        />
                        <span>
                          <ReactMarkdown components={{ p: 'span' }}>
                            {wordItem.why_difficult}
                          </ReactMarkdown>
                        </span>
                      </div>
                    )}
                    {wordItem.forms && wordItem.forms.length > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          flexWrap: 'wrap',
                          marginBottom: 8,
                        }}
                      >
                        <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                          Forms:
                        </span>
                        {wordItem.forms.map((form, fIdx) => (
                          <span
                            key={fIdx}
                            style={{
                              fontSize: '0.76rem',
                              background: 'rgba(103,80,164,0.08)',
                              color: 'var(--liwaza-primary)',
                              padding: '2px 8px',
                              borderRadius: 4,
                            }}
                          >
                            {form}
                          </span>
                        ))}
                      </div>
                    )}
                    {wordItem.sample_sentences &&
                      wordItem.sample_sentences.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            Examples:
                          </span>
                          <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                            {wordItem.sample_sentences.map((s, sIdx) => (
                              <li
                                key={sIdx}
                                style={{
                                  fontSize: '0.82rem',
                                  color: '#666',
                                  lineHeight: 1.6,
                                }}
                              >
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    {wordItem.common_confusions &&
                      wordItem.common_confusions.length > 0 && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            flexWrap: 'wrap',
                            marginBottom: 8,
                          }}
                        >
                          <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            Often confused with:
                          </span>
                          {wordItem.common_confusions.map((w, cIdx) => (
                            <span
                              key={cIdx}
                              style={{
                                fontSize: '0.76rem',
                                background: 'rgba(249,115,22,0.1)',
                                color: '#ea580c',
                                padding: '2px 8px',
                                borderRadius: 4,
                              }}
                            >
                              {w}
                            </span>
                          ))}
                        </div>
                      )}
                    {wordItem.listening_tips && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 8,
                          padding: '8px 12px',
                          background: 'rgba(103,80,164,0.06)',
                          borderRadius: 8,
                          fontSize: '0.82rem',
                          color: 'var(--liwaza-primary)',
                          lineHeight: 1.5,
                        }}
                      >
                        <FiHeadphones style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>
                          <ReactMarkdown components={{ p: 'span' }}>
                            {wordItem.listening_tips}
                          </ReactMarkdown>
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Missed Words Learning Actions (Listening tests only) */}
        {testType === 'listening' && testResults?.missedWordsActions && (
          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              borderRadius: 16,
              border: '1px solid rgba(103,80,164,0.15)',
              overflow: 'hidden',
              marginBottom: 32,
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background:
                  'linear-gradient(90deg, var(--liwaza-primary, #6750a4), #22c55e)',
              }}
            />
            {testResults.missedWordsActions.learning_actions &&
              testResults.missedWordsActions.learning_actions.length > 0 && (
                <>
                  <div
                    style={{
                      padding: 24,
                      borderBottom: '1px solid var(--liwaza-outline-variant)',
                      background: 'var(--liwaza-surface-container-high)',
                    }}
                  >
                    <h2
                      style={{
                        margin: 0,
                        fontSize: 20,
                        fontWeight: 600,
                        color: 'var(--liwaza-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <FiTarget /> Practice Exercises
                    </h2>
                  </div>
                  <div
                    style={{
                      padding: 24,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                    }}
                  >
                    {testResults.missedWordsActions.learning_actions.map(
                      (action, idx) => (
                        <div
                          key={idx}
                          style={{
                            background: '#fafafa',
                            borderRadius: 12,
                            padding: '16px 20px',
                            border: '1px solid rgba(0,0,0,0.06)',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              marginBottom: 6,
                            }}
                          >
                            <span
                              style={{ fontSize: '0.95rem', fontWeight: 700 }}
                            >
                              {action.word}
                            </span>
                            <button
                              onClick={() => pronounceWord(action.word)}
                              title={`Hear pronunciation of "${action.word}"`}
                              aria-label={`Pronounce ${action.word}`}
                              style={{
                                background: 'rgba(103,80,164,0.1)',
                                border: 'none',
                                borderRadius: '50%',
                                width: 28,
                                height: 28,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: 'var(--liwaza-primary, #6750a4)',
                                transition: 'all 0.2s ease',
                                flexShrink: 0,
                              }}
                            >
                              <FiVolume2 size={13} />
                            </button>
                            <span
                              style={{
                                fontSize: '0.72rem',
                                background: 'rgba(34,197,94,0.1)',
                                color: '#16a34a',
                                padding: '2px 8px',
                                borderRadius: 6,
                                fontWeight: 500,
                                textTransform: 'capitalize',
                              }}
                            >
                              {action.action_type?.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <ReactMarkdown
                            components={{
                              p: ({ children }) => (
                                <p
                                  style={{
                                    margin: '0 0 8px',
                                    color: '#666',
                                    fontSize: '0.85rem',
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {children}
                                </p>
                              ),
                            }}
                          >
                            {action.description}
                          </ReactMarkdown>
                          {action.exercises && action.exercises.length > 0 && (
                            <ul style={{ margin: '0 0 0 20px', padding: 0 }}>
                              {action.exercises.map((ex, eIdx) => (
                                <li
                                  key={eIdx}
                                  style={{
                                    fontSize: '0.82rem',
                                    color: '#666',
                                    lineHeight: 1.6,
                                    marginBottom: 4,
                                  }}
                                >
                                  <ReactMarkdown components={{ p: 'span' }}>
                                    {ex}
                                  </ReactMarkdown>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </>
              )}
            {testResults.missedWordsActions.overall_listening_tips &&
              testResults.missedWordsActions.overall_listening_tips.length >
                0 && (
                <div style={{ padding: '0 24px 24px' }}>
                  <h3
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      marginBottom: 8,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <FiHeadphones /> Overall Listening Tips
                  </h3>
                  <ul style={{ margin: '0 0 0 20px', padding: 0 }}>
                    {testResults.missedWordsActions.overall_listening_tips.map(
                      (tip, idx) => (
                        <li
                          key={idx}
                          style={{
                            fontSize: '0.85rem',
                            color: '#666',
                            lineHeight: 1.6,
                            marginBottom: 4,
                          }}
                        >
                          <ReactMarkdown components={{ p: 'span' }}>
                            {tip}
                          </ReactMarkdown>
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}
            {testResults.missedWordsActions.vocabulary_weakness_summary && (
              <div
                style={{
                  margin: '0 24px 24px',
                  padding: '12px 16px',
                  background: 'rgba(103,80,164,0.06)',
                  borderRadius: 10,
                }}
              >
                <ReactMarkdown
                  components={{
                    p: ({ children }) => (
                      <p
                        style={{
                          margin: 0,
                          fontSize: '0.88rem',
                          color: 'var(--liwaza-primary)',
                          lineHeight: 1.5,
                          fontStyle: 'italic',
                        }}
                      >
                        {children}
                      </p>
                    ),
                  }}
                >
                  {testResults.missedWordsActions.vocabulary_weakness_summary}
                </ReactMarkdown>
              </div>
            )}
          </div>
        )}

        {/* Performance Analysis */}
        <div
          style={{
            background: 'var(--liwaza-surface-container)',
            borderRadius: 16,
            border: '1px solid var(--liwaza-outline-variant)',
            padding: 24,
            marginBottom: 32,
          }}
        >
          <h2
            style={{
              margin: '0 0 16px 0',
              fontSize: 20,
              fontWeight: 600,
              color: 'var(--liwaza-primary)',
            }}
          >
            Performance Analysis
          </h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 24,
            }}
          >
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                Strengths
              </h3>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 20,
                  color: 'var(--liwaza-on-surface)',
                }}
              >
                {testResults?.bandScore >= 7 && (
                  <li>Excellent overall performance</li>
                )}
                {testResults?.rawScore >= testResults?.totalQuestions * 0.8 && (
                  <li>Strong accuracy rate</li>
                )}
                <li>Completed the test successfully</li>
              </ul>
            </div>

            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                Areas for Improvement
              </h3>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 20,
                  color: 'var(--liwaza-on-surface)',
                }}
              >
                {testResults?.bandScore < 6.5 && (
                  <li>Focus on improving accuracy</li>
                )}
                {testResults?.rawScore < testResults?.totalQuestions * 0.7 && (
                  <li>Practice more test questions</li>
                )}
                <li>Review incorrect answers for patterns</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
